<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Spinner from 'shared/components/Spinner.vue';
import BillingAPI from 'billing/api';

const { t, locale } = useI18n();

const subscription = ref({});
const plans = ref([]);
const isLoading = ref(true);
const subscribingPlan = ref(null);
const cpfCnpj = ref('');

// Asaas exige CPF/CNPJ do pagador pra emitir a cobranca
const isCpfCnpjValid = computed(() => {
  const digits = cpfCnpj.value.replace(/\D/g, '');
  return digits.length === 11 || digits.length === 14;
});

const fetchBilling = async () => {
  try {
    const { data } = await BillingAPI.getBilling();
    subscription.value = data.subscription || {};
    plans.value = data.plans || [];
  } catch (error) {
    useAlert(t('VINCTO_BILLING.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const status = computed(() => subscription.value.status || 'active');
const statusLabel = computed(() =>
  t(`VINCTO_BILLING.STATUS.${status.value.toUpperCase()}`)
);

// pill tonal por status (classes inteiras pro Tailwind enxergar)
const STATUS_PILL_CLASSES = {
  active: 'bg-n-teal-3 text-n-teal-11',
  trialing: 'bg-n-blue-3 text-n-blue-11',
  past_due: 'bg-n-amber-3 text-n-amber-11',
  expired: 'bg-n-ruby-3 text-n-ruby-11',
  canceled: 'bg-n-slate-3 text-n-slate-11',
};
const statusPillClass = computed(
  () => STATUS_PILL_CLASSES[status.value] || STATUS_PILL_CLASSES.canceled
);

const currentPlanLabel = computed(() => {
  const plan = plans.value.find(p => p.name === subscription.value.plan_name);
  return plan?.display_name || subscription.value.plan_name;
});

const daysLeft = computed(() => {
  if (status.value !== 'trialing' || !subscription.value.current_period_end) {
    return null;
  }
  const diff = subscription.value.current_period_end * 1000 - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
});

const periodEndDate = computed(() => {
  if (!subscription.value.current_period_end) return '';
  return new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'long',
  }).format(new Date(subscription.value.current_period_end * 1000));
});

const formatPrice = price =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);

const isCurrentPlan = plan =>
  status.value === 'active' && subscription.value.plan_name === plan.name;

const subscribe = async plan => {
  if (!isCpfCnpjValid.value) {
    useAlert(t('VINCTO_BILLING.CPF_CNPJ_REQUIRED'));
    return;
  }
  subscribingPlan.value = plan.name;
  try {
    const { data } = await BillingAPI.subscribe(plan.name, cpfCnpj.value);
    if (data.invoice_url) {
      window.open(data.invoice_url, '_blank');
    }
    useAlert(t('VINCTO_BILLING.SUBSCRIBE_SUCCESS'));
    await fetchBilling();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('VINCTO_BILLING.SUBSCRIBE_ERROR')
    );
  } finally {
    subscribingPlan.value = null;
  }
};

const openInvoice = () => {
  window.open(subscription.value.invoice_url, '_blank');
};

onMounted(fetchBilling);
</script>

<template>
  <div class="flex flex-col gap-6 w-full max-w-4xl">
    <BaseSettingsHeader
      :title="$t('VINCTO_BILLING.TITLE')"
      :description="$t('VINCTO_BILLING.DESCRIPTION')"
      feature-name="vincto_billing"
    />
    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner color-scheme="primary" />
    </div>
    <template v-else>
      <div
        class="p-5 bg-white border rounded-2xl border-n-weak dark:bg-n-solid-2"
      >
        <div class="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p class="text-sm text-n-slate-11 mb-1.5">
              {{ $t('VINCTO_BILLING.CURRENT_PLAN') }}
            </p>
            <div class="flex items-center gap-2">
              <p
                class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
              >
                {{ currentPlanLabel || statusLabel }}
              </p>
              <span
                v-if="currentPlanLabel"
                class="px-2 py-0.5 text-xs font-medium rounded-md"
                :class="statusPillClass"
              >
                {{ statusLabel }}
              </span>
            </div>
            <p v-if="daysLeft !== null" class="text-sm text-n-slate-11 mt-1">
              {{ $t('VINCTO_BILLING.TRIAL_DAYS_LEFT', { days: daysLeft }) }}
            </p>
            <p
              v-else-if="status === 'active' && periodEndDate"
              class="text-sm text-n-slate-11 mt-1"
            >
              {{ $t('VINCTO_BILLING.RENEWS_ON', { date: periodEndDate }) }}
            </p>
          </div>
          <NextButton
            v-if="subscription.invoice_url && status === 'past_due'"
            :label="$t('VINCTO_BILLING.OPEN_INVOICE')"
            class="font-semibold"
            @click="openInvoice"
          />
        </div>
      </div>

      <div>
        <h3
          class="text-base font-semibold tracking-[-0.02em] text-n-slate-12 mb-3"
        >
          {{ $t('VINCTO_BILLING.PLANS_TITLE') }}
        </h3>
        <NextInput
          v-model="cpfCnpj"
          type="text"
          class="mb-4 max-w-xs"
          :label="$t('VINCTO_BILLING.CPF_CNPJ_LABEL')"
          :placeholder="$t('VINCTO_BILLING.CPF_CNPJ_PLACEHOLDER')"
        />
        <div class="grid gap-4 sm:grid-cols-2">
          <div
            v-for="plan in plans"
            :key="plan.name"
            class="p-5 bg-white border rounded-2xl dark:bg-n-solid-2 flex flex-col gap-3"
            :class="isCurrentPlan(plan) ? 'border-n-blue-9' : 'border-n-weak'"
          >
            <div class="flex items-baseline justify-between gap-2">
              <p class="text-base font-semibold text-n-slate-12">
                {{ plan.display_name }}
              </p>
              <p class="text-n-slate-12">
                <span class="text-2xl font-semibold tracking-[-0.02em]">
                  {{ formatPrice(plan.price) }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{ $t('VINCTO_BILLING.PER_MONTH') }}
                </span>
              </p>
            </div>
            <ul class="text-sm text-n-slate-11 space-y-1.5 mb-2">
              <li class="flex items-center gap-2">
                <i class="i-lucide-check size-4 flex-shrink-0 text-n-teal-11" />
                {{
                  $t('VINCTO_BILLING.LIMIT_AGENTS', {
                    count: plan.limits?.agents,
                  })
                }}
              </li>
              <li class="flex items-center gap-2">
                <i class="i-lucide-check size-4 flex-shrink-0 text-n-teal-11" />
                {{
                  $t('VINCTO_BILLING.LIMIT_INBOXES', {
                    count: plan.limits?.inboxes,
                  })
                }}
              </li>
            </ul>
            <NextButton
              class="w-full font-semibold mt-auto"
              :label="
                isCurrentPlan(plan)
                  ? $t('VINCTO_BILLING.CURRENT_PLAN_BUTTON')
                  : $t('VINCTO_BILLING.SUBSCRIBE')
              "
              :disabled="isCurrentPlan(plan) || Boolean(subscribingPlan)"
              :is-loading="subscribingPlan === plan.name"
              @click="subscribe(plan)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
